import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ArrowRight, X, Trophy } from "lucide-react";
import { api } from "../lib/api";

const STORAGE_DISMISS = "summit:social-rail-dismissed";

const ACTION_LABELS = {
    adventure_posted: "adventure posts",
    gps_comment: "trail notes",
    save_received: "saves received",
    review_written: "reviews written",
};

/**
 * Discovery chip for the For-You feed:
 *   "Sierra and 4 friends are competing in '3 hikes this month'" → Join
 *
 * Renders quietly:
 *   - Hidden if no friends are participating in any active challenge
 *   - Hidden if the user explicitly dismissed the chip (per-day storage)
 *   - Auto-rotates between up to 3 candidate challenges every 6s
 */
export default function FriendChallengeSocialChip() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(0);
    const [dismissed, setDismissed] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_DISMISS);
            if (!raw) return false;
            const { day } = JSON.parse(raw);
            return day === new Date().toISOString().slice(0, 10);
        } catch {
            return false;
        }
    });

    useEffect(() => {
        let cancel = false;
        api.get("/friend-challenges/social-rail/feed")
            .then(({ data }) => { if (!cancel) setItems(data.items || []); })
            .catch(() => {});
        return () => { cancel = true; };
    }, []);

    useEffect(() => {
        if (items.length <= 1) return;
        const t = setInterval(() => setActive((i) => (i + 1) % items.length), 6000);
        return () => clearInterval(t);
    }, [items.length]);

    if (dismissed || items.length === 0) return null;
    const c = items[active];
    if (!c) return null;

    const dismiss = () => {
        try {
            localStorage.setItem(STORAGE_DISMISS, JSON.stringify({ day: new Date().toISOString().slice(0, 10) }));
        } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <Link
            to={`/friend-challenges/${c.challenge_id}`}
            data-testid="social-rail-chip"
            className="relative block bg-snowcap border border-ridge-line rounded-xl px-4 py-3 hover:border-ember-orange transition-all group"
        >
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); dismiss(); }}
                aria-label="Dismiss"
                data-testid="social-rail-dismiss"
                className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-trail-tan/40 flex items-center justify-center text-slate-gray"
            >
                <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-3 pr-8">
                <FacePile users={c.friends_sample} />
                <div className="flex-1 min-w-0">
                    <p data-testid="social-rail-headline" className="text-sm text-granite leading-tight">
                        <span className="font-bold text-midnight-pine">
                            {headline(c)}
                        </span>{" "}
                        in{" "}
                        <span className="font-display font-black text-midnight-pine">
                            "{c.title}"
                        </span>
                    </p>
                    <p className="text-[11px] text-slate-gray mt-0.5">
                        Goal: {c.target_count} {ACTION_LABELS[c.action_kind] || c.action_kind}
                        {c.is_member && <span className="ml-2 inline-flex items-center gap-1 text-moss font-bold uppercase tracking-widest text-[9px]">
                            <Trophy className="w-2.5 h-2.5" /> You're in
                        </span>}
                    </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ember-orange text-white font-bold text-xs group-hover:bg-ember-dark active:scale-95 transition-all">
                    {c.is_member ? "View" : "Join"} <ArrowRight className="w-3 h-3" />
                </span>
            </div>
            {items.length > 1 && (
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                    {items.map((_, i) => (
                        <span key={i} className={`w-1 h-1 rounded-full transition-all ${i === active ? "bg-ember-orange w-3" : "bg-ridge-line"}`} />
                    ))}
                </div>
            )}
        </Link>
    );
}

function headline(c) {
    const sample = c.friends_sample || [];
    const others = Math.max(0, c.friends_in_count - 1);
    const firstName = sample[0]?.name?.split(" ")[0] || "Someone you follow";
    if (c.friends_in_count <= 1) {
        return `${firstName} is in this challenge`;
    }
    if (others === 1) {
        return `${firstName} and 1 friend are competing`;
    }
    return `${firstName} and ${others} friends are competing`;
}

function FacePile({ users = [] }) {
    if (!users.length) {
        return (
            <div className="w-10 h-10 rounded-full bg-trail-tan flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-slate-gray" />
            </div>
        );
    }
    return (
        <div className="flex -space-x-2 shrink-0">
            {users.slice(0, 3).map((u) => (
                <div
                    key={u.user_id}
                    title={u.name}
                    className="w-9 h-9 rounded-full bg-trail-tan border-2 border-snowcap overflow-hidden flex items-center justify-center text-xs font-bold text-midnight-pine"
                >
                    {u.avatar
                        ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                        : (u.name || "?").charAt(0).toUpperCase()}
                </div>
            ))}
        </div>
    );
}
